import { useMutation, type MutateResult } from "@vue/apollo-composable"
import { computed, reactive } from "vue"

import { type FilterableColumn } from "@/types"
import {
  type MutationRootCreateTableFilterArgs,
  type MutationRootRemoveTableFiltersArgs,
  type MutationRootSetDefaultFilterArgs,
  type MutationRootUnsetDefaultFilterArgs,
  type MutationRootUpdateTableFilterArgs,
  type QueryRootTableFiltersArgs,
  type TableFilter,
  type TableFilterList,
  type TableFilterTable,
} from "@/graphql/types"
import CreateTablePresetMutation from "@/graphql/preset/filter/CreateTableFilterPreset.gql"
import RemoveTablePresetsMutation from "@/graphql/preset/filter/RemoveTableFilterPresets.gql"
import SetDefaultTablePresetMutation from "@/graphql/preset/filter/SetDefaultTableFilterPreset.gql"
import TablePresetListQuery from "@/graphql/preset/filter/TableFilterPresetList.gql"
import UnsetDefaultTablePresetMutation from "@/graphql/preset/filter/UnsetDefaultTableFilterPreset.gql"
import UpdateTablePresetMutation from "@/graphql/preset/filter/UpdateTableFilterPreset.gql"
import { useSessionStore } from "@/store/session"
import { useApi } from "@/utils/composables/apollo/useApi"
import { useTablePresetConfigurationShared } from "@/utils/composables/useTablePresetConfigurationShared"
import { useOnNextResult } from "@/utils/misc"

export type TableFilterPresetListResult = { tableFilters: TableFilterList }
export type TableFilterPresetCreateResult = { createTableFilter: TableFilter }
export type TableFilterPresetUpdateResult = { updateTableFilter: TableFilter }
export type TableFilterPresetRemoveResult = { removeTableFilters: number }
export type SetDefaultTableFilterPresetResult = { setDefaultFilter: boolean }
export type UnsetDefaultTableFilterPresetResult = { unsetDefaultFilter: boolean }

export const tableFilterPresetResultMap = {
  getList: (result: TableFilterPresetListResult) => result.tableFilters,
  getCreated: (result: TableFilterPresetCreateResult) => result.createTableFilter,
  getUpdated: (result: TableFilterPresetUpdateResult) => result.updateTableFilter,
  getRemovedCount: (result: TableFilterPresetRemoveResult) => result.removeTableFilters,
  getLinkedCount: undefined,
}

export function useFilterPreset<TData, TMappedData = TData>(
  table: TableFilterTable | undefined,
  filterPresetColumns: FilterableColumn<TData, TMappedData>[],
  listRouteName: string | string[]
) {
  const sessionStore = useSessionStore()

  const sharedTablePresetConfiguration = useTablePresetConfigurationShared(
    table,
    filterPresetColumns,
    listRouteName
  )

  const {
    listQueryVariables,
    listQueryOptions,

    initialized,

    presets,
    isPresetLoaded,
    selectedPresetId,

    selectedColumns,
  } = sharedTablePresetConfiguration

  const selectedColumnFields = computed(
    () =>
      new Set(
        selectedColumns.value.flatMap(
          (c) => c.fields ?? [(c as FilterableColumn<TData, TMappedData>).dataKey]
        )
      )
  )

  const api = useApi<
    TableFilter,
    "tableFilters",
    TableFilterPresetListResult,
    QueryRootTableFiltersArgs,
    undefined,
    object,
    TableFilterPresetCreateResult,
    MutationRootCreateTableFilterArgs,
    TableFilterPresetUpdateResult,
    MutationRootUpdateTableFilterArgs,
    TableFilterPresetRemoveResult,
    MutationRootRemoveTableFiltersArgs
  >({
    typename: "TableFilter",
    operations: {
      list: TablePresetListQuery,
      getById: undefined,
      create: CreateTablePresetMutation,
      update: UpdateTablePresetMutation,
      remove: RemoveTablePresetsMutation,
      link: undefined,
    },
    resultMap: tableFilterPresetResultMap,
    mapRemovedIds: (variables) => variables.ids,
    listQueryVariables,
    listQueryOptions,
  })

  api.addRemoveReducer(api.getListFilterRemoveReducer((ids) => (item) => !ids.includes(item.id)))

  api.onResult((result) => (presets.value = result.data?.tableFilters?.items ?? []))

  void api.whenListResultAvailable.then(async () => {
    if (filterPresetColumns.length) {
      await useOnNextResult(initialized)
      isPresetLoaded.value = true
    }
  })

  const defaultLoading = computed(() => setDefaultLoading.value || unsetDefaultLoading.value)

  const { mutate: setDefaultFilter, loading: setDefaultLoading } = useMutation<
    SetDefaultTableFilterPresetResult,
    MutationRootSetDefaultFilterArgs
  >(SetDefaultTablePresetMutation, {
    errorPolicy: "all",
    update: api.prepareListCacheReducer((cachedQuery, _data, variables) => {
      const list = tableFilterPresetResultMap.getList(cachedQuery)
      for (const item of list.items) item.isUserDefault = item.id === variables.id
      if (sessionStore.isUserAdmin) {
        for (const item of list.items) item.isGlobalDefault = item.id === variables.id
      }
      return cachedQuery
    }),
  })

  const { mutate: unsetDefaultFilter, loading: unsetDefaultLoading } = useMutation<
    UnsetDefaultTableFilterPresetResult,
    MutationRootUnsetDefaultFilterArgs
  >(UnsetDefaultTablePresetMutation, {
    errorPolicy: "all",
    update: api.prepareListCacheReducer((cachedQuery) => {
      const list = tableFilterPresetResultMap.getList(cachedQuery)
      for (const item of list.items) item.isUserDefault = false
      return cachedQuery
    }),
  })

  async function create(
    name: string,
    columns: string[]
  ): MutateResult<TableFilterPresetCreateResult> {
    if (!table) throw Error("Table is not provided!")

    const result = await api.create({ name, table, columns })
    selectedPresetId.value = result?.data?.createTableFilter.id
    return result
  }

  async function setDefault(id: string): MutateResult<SetDefaultTableFilterPresetResult> {
    if (!table) throw Error("Table is not provided!")
    return await setDefaultFilter({ table, id })
  }

  async function unsetDefault(): MutateResult<UnsetDefaultTableFilterPresetResult> {
    if (!table) throw Error("Table is not provided!")
    return await unsetDefaultFilter({ table })
  }

  return reactive({
    filter: {
      table,
      api,
      defaultLoading,

      selectedColumnFields,
      ...sharedTablePresetConfiguration,

      create,
      setDefault,
      unsetDefault,
    },
  })
}
